import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useRegister } from "../../hooks/useAuth.ts";
import { useToast } from "../../components/common/ToastContext.tsx";
import { useTheme } from "../../contexts/ThemeContext.tsx";

const Signup = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { themeName } = useTheme();
  const [loading, setLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirm_password: "",
    role: ""
  });

  // Form validation states
  const [errors, setErrors] = useState({
    name: "",
    email: "",
    password: "",
    confirm_password: "",
    role: ""
  });

  const registerMutation = useRegister();

  // Function to toggle both password fields at once
  const toggleBothPasswords = () => {
    setShowPassword(!showPassword);
    setShowConfirmPassword(!showPassword);
  };

  // Helper function to validate email format
  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Helper function to validate password strength
  const validatePassword = (password: string) => {
    if (password.length < 8) {
      return "Password must be at least 8 characters long";
    }
    if (!/[A-Z]/.test(password)) {
      return "Password must include at least one uppercase letter";
    }
    if (!/[0-9]/.test(password)) {
      return "Password must include at least one number";
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      return "Password must include at least one special character";
    }
    return "";
  };

  // Effect to clear errors when form data changes
  useEffect(() => {
    setErrors({
      name: "",
      email: "",
      password: "",
      confirm_password: "",
      role: ""
    });
  }, [formData]);

  // Check form validity whenever form data changes
  useEffect(() => {
    const isValid = formData.name.trim() !== "" &&
      formData.email.trim() !== "" &&
      formData.password !== "" &&
      formData.confirm_password !== "" &&
      formData.role !== "" &&
      formData.password === formData.confirm_password &&
      validatePassword(formData.password) === "" &&
      validateEmail(formData.email);
    setIsFormValid(isValid);
  }, [formData]);

  // Check form completion status
  const isFormComplete = () => {
    return (
      formData.name.trim() !== "" &&
      formData.email.trim() !== "" &&
      formData.password !== "" &&
      formData.confirm_password !== "" &&
      formData.role !== ""
    );
  };

  // Validate form before submission
  const validateForm = () => {
    let isValid = true;
    const newErrors = {
      name: "",
      email: "",
      password: "",
      confirm_password: "",
      role: ""
    };

    // Check if all required fields are filled
    if (!isFormComplete()) {
      showToast("Please fill all required fields", "warning");

      // Mark all empty fields as errors
      if (!formData.name.trim()) newErrors.name = "Name is required";
      if (!formData.email.trim()) newErrors.email = "Email is required";
      if (!formData.password) newErrors.password = "Password is required";
      if (!formData.confirm_password) newErrors.confirm_password = "Please confirm your password";
      if (!formData.role) newErrors.role = "Please select a role";

      setErrors(newErrors);
      return false;
    }

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
      isValid = false;
      showToast("Name is required", "warning");
    } else if (formData.name.length < 2) {
      newErrors.name = "Name must be at least 2 characters";
      isValid = false;
      showToast("Name must be at least 2 characters", "warning");
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
      isValid = false;
      showToast("Email is required", "warning");
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Please enter a valid email address";
      isValid = false;
      showToast("Please enter a valid email address", "warning");
    }

    // Password validation
    const passwordError = validatePassword(formData.password);
    if (!formData.password) {
      newErrors.password = "Password is required";
      isValid = false;
      showToast("Password is required", "warning");
    } else if (passwordError) {
      newErrors.password = passwordError;
      isValid = false;
      showToast(passwordError, "warning");
    }

    // Password confirmation
    if (formData.password !== formData.confirm_password) {
      newErrors.confirm_password = "Passwords do not match";
      isValid = false;
      showToast("Passwords do not match", "error");
    }

    // Role validation
    if (!formData.role) {
      newErrors.role = "Please select a role";
      isValid = false;
      showToast("Please select a role", "warning");
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      return;
    }

    const formDataObject = new FormData();
    formDataObject.append("name", formData.name);
    formDataObject.append("email", formData.email);
    formDataObject.append("password", formData.password);
    formDataObject.append("confirm_password", formData.confirm_password);
    formDataObject.append("role", formData.role);

    setLoading(true);

    try {
      await registerMutation.mutateAsync(formDataObject, {
        onSuccess: () => {
          // Show success toast with longer duration
          showToast("Registration successful! Please sign in now.", "success", 5000);

          // Set success state to show success animation
          setRegistrationSuccess(true);
          setLoading(false); // Stop loading indicator

          // Add a small delay before redirecting to login page
          // This gives the user time to see the success message
          setTimeout(() => {
            // Show another toast before redirecting
            showToast("Redirecting to login page...", "info", 2000);

            // Redirect after delay
            setTimeout(() => {
              navigate("/login");
            }, 1500);
          }, 2000);
        },
        onError: (err: any) => {
          console.error("Registration error:", err);

          // Handle different error scenarios
          if (err.response) {
            // The server responded with a status code outside the 2xx range
            const errorMessage = err.response.data?.message ||
              err.response.data?.error ||
              `Registration failed: ${err.response.statusText}`;
            showToast(errorMessage, "error");

            // If the error is about email already in use
            if (err.response.data?.message?.toLowerCase().includes("email") &&
              err.response.data?.message?.toLowerCase().includes("exists")) {
              showToast("This email is already registered. Please login or use a different email.", "error");
            }
          } else if (err.request) {
            // The request was made but no response was received
            showToast("Network error. Please check your internet connection and try again.", "error");
          } else {
            // Something happened in setting up the request
            showToast("An unexpected error occurred. Please try again later.", "error");
          }
          setLoading(false);
        }
      });

    } catch (err: any) {
      // Handled in onError
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 ${themeName === 'light' ? 'bg-gradient-to-r from-primary to-success' : themeName === 'dark' ? 'bg-gradient-to-r from-primary to-background' : 'bg-primary'}`}>
      <div className={`w-full max-w-5xl rounded-xl shadow-2xl overflow-hidden ${['light', 'brand', 'pastel'].includes(themeName) ? 'bg-white' : 'bg-primary border border-gray-300'}`}>
        <div className="flex flex-col md:flex-row">
          {/* Left Side - Logo and Brand */}
          <div className={`w-full md:w-1/2 p-8 flex flex-col items-center justify-center text-center ${themeName === 'light' ? 'bg-gradient-to-br from-primary to-success' : themeName === 'dark' ? 'bg-background' : 'bg-primary'}`}>
            <img
              src="/logo.png"
              alt="Socialsleft Collab Logo"
              className="w-32 h-32 mb-6"
            />
            <h1 className="text-4xl font-bold text-white mb-4">SocialsCollab</h1>
            <p className="text-white/90 text-lg">Join our community today</p>
          </div>

          {/* Right Side - Signup Form */}
          <div className={`w-full md:w-1/2 p-8 ${themeName === 'dark' ? 'bg-background' : ['light', 'brand', 'pastel'].includes(themeName) ? 'bg-white' : 'bg-primary'}`}>
            <h2 className={`text-2xl font-bold text-center mb-6 ${['light', 'brand', 'pastel'].includes(themeName) ? 'text-gray-800' : 'text-white'}`}>
              Create Account
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className={`text-sm font-medium block ${['light', 'brand', 'pastel'].includes(themeName) ? 'text-gray-700' : 'text-white'}`}>
                  Username
                </label>
                <input
                  type="text"
                  className={`mt-1 block w-full px-3 py-2 border ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary`}
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  required
                />
                {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
              </div>

              <div>
                <label className={`text-sm font-medium block ${['light', 'brand', 'pastel'].includes(themeName) ? 'text-gray-700' : 'text-white'}`}>
                  Email
                </label>
                <input
                  type="email"
                  className={`mt-1 block w-full px-3 py-2 border ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500`}
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  required
                />
                {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
              </div>

              <div>
                <label className={`text-sm font-medium block ${['light', 'brand', 'pastel'].includes(themeName) ? 'text-gray-700' : 'text-white'}`}>
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    className={`mt-1 block w-full px-3 py-2 border ${errors.password ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 pr-10`}
                    value={formData.password}
                    onChange={(e) =>
                      setFormData({ ...formData, password: e.target.value })
                    }
                    required
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700 focus:outline-none mt-1"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
                {formData.password && !errors.password && (
                  <p className="mt-1 text-xs text-gray-600">
                    Password must have at least 8 characters, 1 uppercase letter, 1 number, and 1 special character.
                  </p>
                )}
              </div>

              <div>
                <label className={`text-sm font-medium block ${['light', 'brand', 'pastel'].includes(themeName) ? 'text-gray-700' : 'text-white'}`}>
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    className={`mt-1 block w-full px-3 py-2 border ${errors.confirm_password ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 pr-10`}
                    value={formData.confirm_password}
                    onChange={(e) =>
                      setFormData({ ...formData, confirm_password: e.target.value })
                    }
                    required
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700 focus:outline-none mt-1"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                {errors.confirm_password && <p className="mt-1 text-sm text-red-600">{errors.confirm_password}</p>}
                {formData.password && formData.confirm_password && formData.password === formData.confirm_password && !errors.confirm_password && (
                  <p className="mt-1 text-xs text-green-600">Passwords match</p>
                )}
              </div>

              {/* Show password toggle checkbox */}
              <div className="flex items-center">
                <input
                  id="showBothPasswords"
                  type="checkbox"
                  className="h-4 w-4 text-primary-500 focus:ring-primary-500 border-gray-300 rounded"
                  checked={showPassword && showConfirmPassword}
                  onChange={() => {
                    const newState = !showPassword;
                    setShowPassword(newState);
                    setShowConfirmPassword(newState);
                  }}
                />
                <label htmlFor="showBothPasswords" className={`ml-2 block text-sm ${['light', 'brand', 'pastel'].includes(themeName) ? 'text-gray-700' : 'text-white'}`}>
                  Show passwords
                </label>
              </div>

              <div>
                <label className={`text-sm font-medium block ${['light', 'brand', 'pastel'].includes(themeName) ? 'text-gray-700' : 'text-white'}`}>
                  Select Role
                </label>
                <select
                  className={`mt-1 block w-full px-3 py-2 border ${errors.role ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500`}
                  required
                  value={formData.role}
                  onChange={(e) =>
                    setFormData({ ...formData, role: e.target.value })
                  }
                >
                  <option value="" disabled>
                    Select role
                  </option>
                  <option value="ORGANIZATION">Organization</option>
                  {/* <option value="USER">User</option> */}
                </select>
                {errors.role && <p className="mt-1 text-sm text-red-600">{errors.role}</p>}
              </div>

              <button
                type="submit"
                className={`w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-white
                focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all duration-300
                flex items-center justify-center
                ${loading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : isFormValid
                      ? 'bg-success hover:bg-success transform hover:scale-[1.02]'
                      : 'bg-primary hover:bg-success transform hover:scale-[1.02]'}`}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating Account...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd"></path>
                    </svg>
                    Create Account
                  </>
                )}
              </button>

              {/* Password strength indicator */}
              {formData.password && (
                <div className="mt-2">
                  <p className="text-xs text-gray-500 mb-1">Password strength:</p>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${validatePassword(formData.password) ? 'bg-red-500 w-1/4' :
                        formData.password.length >= 10 ? 'bg-green-500 w-full' :
                          formData.password.length >= 8 ? 'bg-yellow-500 w-3/4' : 'bg-orange-500 w-1/2'
                        }`}
                    ></div>
                  </div>
                </div>
              )}
            </form>

            <p className="mt-8 text-center">
              <span className={` ${['light', 'brand', 'pastel'].includes(themeName) ? 'text-gray-600' : 'text-white'}`}>Already have an account?</span>
              <Link
                to="/login"
                className={`ml-2 font-medium transition-all duration-300 hover:underline ${['light', 'brand', 'pastel'].includes(themeName) ? 'text-primary hover:text-primary' : 'text-white hover:text-gray-300'}`}
              >
                Sign in
              </Link>
            </p>

            {/* Loading animation - shown during form submission */}
            {loading && (
              <div className="mt-4 flex justify-center">
                <div className="animate-pulse text-center p-2 rounded-lg bg-blue-50 text-blue-700">
                  <p className="text-sm">Securely creating your account...</p>
                </div>
              </div>
            )}

            {/* Success animation - only shown after successful registration */}
            {registrationSuccess && (
              <div className="mt-4">
                <div className="text-center p-4 rounded-lg bg-green-50 border border-green-200 shadow-md">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                    <svg className="w-10 h-10 text-green-500" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-green-800 mb-1">Registration Successful!</h3>
                  <p className="text-green-600">Your account has been created successfully.</p>
                  <p className="text-sm text-green-500 mt-2">Redirecting to login page...</p>
                  <div className="mt-3 h-1 w-full bg-gray-200 rounded-full overflow-hidden">
                    <div className="animate-progressBar h-full bg-green-500 rounded-full"></div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
